import * as React from "react"

/** A line is about this many pixels, for a mouse that scrolls by lines. */
const LINE_HEIGHT = 16

/**
 * A plain wheel (or a two-finger trackpad swipe) moves the page, for a viewer
 * whose frame does not scroll itself: the page is transformed inside it, so
 * without this the wheel would do nothing at all. Shift turns a vertical wheel
 * sideways, as browsers do for a scrolling element.
 *
 * Ctrl/⌘ + wheel is left alone; that is a zoom (see `useCtrlWheelZoom`).
 * `onPan` receives the distance to move the page by and returns whether it
 * moved; only then is the browser's own scroll prevented, so a wheel over a
 * page with nowhere to go still scrolls whatever is around it.
 */
export function useWheelPan(
  ref: React.RefObject<HTMLElement | null>,
  onPan: (dx: number, dy: number) => boolean,
): void {
  const onPanRef = React.useRef(onPan)
  React.useLayoutEffect(() => {
    onPanRef.current = onPan
  })

  React.useEffect(() => {
    const el = ref.current
    if (!el) return
    const onWheel = (event: WheelEvent): void => {
      if (event.ctrlKey || event.metaKey) return
      const unit = event.deltaMode === WheelEvent.DOM_DELTA_LINE ? LINE_HEIGHT : 1
      let dx = event.deltaX * unit
      let dy = event.deltaY * unit
      if (event.shiftKey && dx === 0) {
        dx = dy
        dy = 0
      }
      // The page moves against the wheel: wheeling down brings the lower part into view.
      // `|| 0` folds a negated zero back to 0.
      if (onPanRef.current(-dx || 0, -dy || 0)) event.preventDefault()
    }
    el.addEventListener("wheel", onWheel, { passive: false })
    return () => {
      el.removeEventListener("wheel", onWheel)
    }
  }, [ref])
}
